import cx from 'classnames'
import { useCallback, useEffect, useState } from 'react'

import { Copy, useCopy } from '@mondough/copy'

import { useConsentPreferences } from '../react'
import styles from './banner.module.scss'
import { Button } from './components/button'
import { Dialog, type DialogProps } from './components/dialog'

export type CookieConsentBannerProps = Omit<
  DialogProps,
  'open' | 'children'
> & { onCustomise?: () => void; cookiePolicyUrl?: string | URL }

export function CookieConsentBanner({
  className,
  onDismiss,
  onCustomise,
  cookiePolicyUrl,
  ...dialogProps
}: CookieConsentBannerProps) {
  const c = useCopy()
  const { acknowledged, allowAll, rejectAll } = useConsentPreferences()
  const [isOpen, setOpen] = useState(!acknowledged)

  if (!cookiePolicyUrl) {
    cookiePolicyUrl =
      'https://monzo.com' +
      c.string({
        key: 'consent-management.cookie-banner.cookie-notice-url',
        message: '/legal/cookie-notice',
      })
  }

  const handleDismiss = useCallback(
    (event?: Event) => {
      setOpen(false)
      onDismiss?.(event)
    },
    [onDismiss],
  )

  const handleCustomise = useCallback(() => {
    handleDismiss()
    onCustomise?.()
  }, [onCustomise, handleDismiss])

  useEffect(() => {
    if (acknowledged && isOpen) handleDismiss()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acknowledged])

  const title = c({
    key: 'consent-management.cookie-banner.title',
    message: 'Can we use optional cookies?',
  }) as string

  return (
    <Dialog
      {...dialogProps}
      className={cx(styles.dialog, className)}
      open={isOpen}
      aria-labelledby="cookie-consent-banner-title"
      data-testid="cookie-banner"
      // https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag#data-nosnippet-attr
      data-nosnippet
    >
      <h2 id="cookie-consent-banner-title">{title}</h2>
      <p>
        <Copy.Simple
          id="consent-management.cookie-banner.summary"
          fallback="We’re not talking about the crunchy, tasty kind. These cookies help us keep our website safe, give you a better experience and show more relevant ads. You can learn more about <Link>our cookie policy</Link>."
          components={{
            Link: ({ children }) => (
              <a
                href={cookiePolicyUrl.toString()}
                target="_blank"
                rel="noopener noreferrer"
                data-monzoanalytics-no-tracking
              >
                {children}
              </a>
            ),
          }}
        />
      </p>
      <div className={styles.buttons}>
        <Button
          onClick={allowAll}
          theme="primary"
          data-testid="accept-cookies"
          data-monzoanalytics-no-tracking
        >
          <Copy.Simple
            id="consent-management.cookie-banner.allow-all"
            fallback="Accept all"
          />
        </Button>
        <Button
          onClick={rejectAll}
          theme="primary"
          data-testid="reject-cookies"
          data-monzoanalytics-no-tracking
        >
          <Copy.Simple
            id="consent-management.cookie-banner.reject-all"
            fallback="Reject all"
          />
        </Button>
        <Button
          onClick={handleCustomise}
          theme="secondary"
          data-testid="customise-cookies"
          data-monzoanalytics-no-tracking
        >
          <Copy.Simple
            id="consent-management.cookie-banner.customise"
            fallback="Customise"
          />
        </Button>
      </div>
    </Dialog>
  )
}
